import { Reducer } from "redux"
import { InAppCommsActionTypes, InAppCommsState } from "./in-app-comms-types"
import { DeliveryPayload, DeliveryPreferences, InteractionEvent } from "../../model/in-app-comms-model"

const initialState: InAppCommsState = {
    loading: false,
    error: null,
    unavailable: false,
    banner: null,
    modal: null,
    preferences: null,
    pendingEvents: [],
    dismissedModalIds: [],
}

export const inAppCommsReducer: Reducer<InAppCommsState> = (
    state: InAppCommsState = initialState,
    action: any
) => {
    switch (action.type) {
        case InAppCommsActionTypes.API_ATTEMPT:
            return Object.assign({}, state, { loading: true })

        case InAppCommsActionTypes.API_FAILURE:
            return Object.assign({}, state, { loading: false, error: action.payload })

        case InAppCommsActionTypes.SET_UNAVAILABLE:
            // Feature not configured on this server (HTTP 501). Clear any
            // transient error and message content so nothing renders, and mark
            // the feature unavailable so the UI can show a stable message.
            return Object.assign({}, state, {
                loading: false,
                error: null,
                unavailable: true,
                banner: null,
                modal: null,
            })

        case InAppCommsActionTypes.SET_DELIVERY_PAYLOAD: {
            const payload: DeliveryPayload = action.payload
            // Guard against a degraded server response where modals is absent.
            // Also skip any modal the user has already dismissed this session so
            // that a poll arriving before the server processes the dismiss does
            // not re-populate the overlay.
            const nextModal = payload.modals?.items?.find(
                m => !state.dismissedModalIds.includes(m.inappMessageId)
            ) ?? null
            return Object.assign({}, state, {
                loading: false,
                error: null,
                unavailable: false,
                banner: payload.banner ?? null,
                modal: nextModal,
            })
        }

        case InAppCommsActionTypes.SET_PREFERENCES: {
            const preferences: DeliveryPreferences = action.payload
            return Object.assign({}, state, { preferences })
        }

        case InAppCommsActionTypes.QUEUE_EVENT: {
            const event: InteractionEvent = action.payload
            return Object.assign({}, state, {
                pendingEvents: [...state.pendingEvents, event],
            })
        }

        case InAppCommsActionTypes.DEQUEUE_EVENTS: {
            const sent = new Set<string>(action.payload as string[])
            return Object.assign({}, state, {
                pendingEvents: state.pendingEvents.filter(e => !sent.has(e.eventId)),
            })
        }

        case InAppCommsActionTypes.CLEAR_EVENT_QUEUE:
            return Object.assign({}, state, { pendingEvents: [] })

        case InAppCommsActionTypes.DISMISS_MODAL:
            return Object.assign({}, state, {
                modal: null,
                dismissedModalIds: action.payload
                    ? [...state.dismissedModalIds, action.payload as string]
                    : state.dismissedModalIds,
            })

        default:
            return state
    }
}
